import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import './TransactionPage.css';
import './MiddleOfficePages.css';
import './LimitSetupPage.css';

// Highest middle-office tier this user holds, mirroring the backend's
// getMiddleOfficeTier (utils/workflowStageAuth.js): a middle_office_user's
// submissions are staged for approval, while officer/manager/admin save
// directly and may approve or reject others' proposals.
function getMiddleOfficeTier() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role === 'admin') return 'admin';
    const rank = { middle_office_user: 1, middle_office_officer: 2, middle_office_manager: 3 };
    let best = null;
    for (const a of user.assignments || []) {
      if (rank[a.role] && (!best || rank[a.role] > rank[best])) best = a.role;
    }
    return best;
  } catch {
    return null;
  }
}

const currencyOptions = [
  'USD', 'LKR', 'EUR', 'GBP', 'JPY', 'INR', 'CNY', 'SGD', 'AUD', 'CAD', 'CHF', 'HKD', 'NZD', 'SEK', 'ZAR'
];

const productFields = [
  { key: 'product_transaction_limit', label: 'Transaction Limit' },
  { key: 'product_money_market_limit', label: 'Money Market Limit' },
  { key: 'product_fx_limit', label: 'FX Limit' },
  { key: 'product_derivative_limit', label: 'Derivative Limit' },
  { key: 'product_repo_limit', label: 'Repo Limit' },
  { key: 'product_reverse_repo_limit', label: 'Reverse Repo Limit' },
  { key: 'product_gsec_limit', label: 'Gsec Limit' },
  { key: 'product_sell_and_buy_back_limit', label: 'Sell and Buy Back Limit' },
  { key: 'product_buy_and_sell_back_limit', label: 'Buy and Sell Back Limit' },
];

const otherLimitFields = [
  { key: 'tenor_limit', label: 'Tenor Limit' },
  { key: 'settlement_risk_limit', label: 'Settlement Risk Limit' },
  { key: 'country_limit', label: 'Country Limit' },
  { key: 'group_limit', label: 'Group Limit' },
  { key: 'intraday_limit', label: 'Intraday Limit' },
];

const initialState = {
  counterparty_id: '',
  counterparty_type: '',
  overall_exposure_limit: '',
  currency_limit: '',
  product_transaction_limit: '',
  product_money_market_limit: '',
  product_fx_limit: '',
  product_derivative_limit: '',
  product_repo_limit: '',
  product_reverse_repo_limit: '',
  product_gsec_limit: '',
  product_sell_and_buy_back_limit: '',
  product_buy_and_sell_back_limit: '',
  tenor_limit: '',
  settlement_risk_limit: '',
  country_limit: '',
  group_limit: '',
  intraday_limit: '',
  currency: 'LKR'
};

const COUNTERPARTY_GROUPS = [
  { type: 'individual', label: 'Individual' },
  { type: 'joint', label: 'Joint' },
  { type: 'corporate', label: 'Corporate' },
];

export default function LimitSetupPage() {
  const [counterparties, setCounterparties] = useState([]);
  const [form, setForm] = useState(initialState);
  const [toast, setToast] = useState(null); // { type: 'success' | 'danger', message }
  const [loadingCounterparties, setLoadingCounterparties] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const tier = useMemo(getMiddleOfficeTier, []);
  const isApprover = tier === 'admin' || tier === 'middle_office_officer' || tier === 'middle_office_manager';

  const fetchPending = async () => {
    if (!isApprover) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/limit-setup/pending`, { headers: getAuthHeaders() });
      setPending(res.data?.success ? (res.data.data || []) : []);
    } catch (err) {
      setPending([]);
    }
  };

  const fetchCounterparties = async () => {
    setLoadingCounterparties(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/limit-setup/limit-counterparties`, {
        headers: getAuthHeaders()
      });
      setCounterparties(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching counterparties:', err);
      setCounterparties([]);
      setLoadError(
        err?.response?.data?.error || err?.message || 'Could not load counterparties.'
      );
    } finally {
      setLoadingCounterparties(false);
    }
  };

  useEffect(() => {
    fetchCounterparties();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCounterpartyChange = (e) => {
    const [id, type] = e.target.value.split('__');
    setForm(prev => ({ ...prev, counterparty_id: id || '', counterparty_type: type || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/limit-setup/limits`, form, {
        headers: getAuthHeaders()
      });
      showToast(
        'success',
        res.data?.pendingApproval
          ? 'Submitted for approval. A Middle Office Officer or Manager has been notified.'
          : 'Limit saved successfully.'
      );
      setForm(initialState);
      fetchPending();
    } catch (err) {
      console.error('Error saving limit:', err);
      showToast(
        'danger',
        err?.response?.data?.error || err?.message || 'Could not save the limit.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(`${API_BASE_URL}/limit-setup/${id}/approve`, {}, { headers: getAuthHeaders() });
      showToast('success', 'Proposal approved and the limit has been created.');
      fetchPending();
    } catch (err) {
      showToast('danger', err?.response?.data?.error || 'Failed to approve proposal.');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/limit-setup/${id}/reject`,
        { reason: rejectReason },
        { headers: getAuthHeaders() }
      );
      showToast('success', 'Proposal rejected.');
      setRejectingId(null);
      setRejectReason('');
      fetchPending();
    } catch (err) {
      showToast('danger', err?.response?.data?.error || 'Failed to reject proposal.');
    }
  };

  // Every limit field carried in a proposal, so an approver can see exactly
  // what is being requested before approving. Built from the same field lists
  // the form renders, so the two can't drift apart.
  const detailFields = [
    { key: 'overall_exposure_limit', label: 'Overall Exposure Limit' },
    { key: 'currency_limit', label: 'Currency Limit' },
    ...productFields,
    ...otherLimitFields
  ];

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const n = Number(value);
    return isFinite(n)
      ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : String(value);
  };

  const counterpartyName = (proposal) => {
    const match = counterparties.find(
      (cp) => String(cp.id) === String(proposal.counterparty_id) && cp.type === proposal.counterparty_type
    );
    return match ? match.name : `${proposal.counterparty_type} #${proposal.counterparty_id}`;
  };

  const renderNumberField = (field) => (
    <div className="form-group" key={field.key}>
      <label className="form-label" htmlFor={field.key}>{field.label}</label>
      <input
        id={field.key}
        className="form-control"
        type="number"
        min="0"
        step="0.01"
        placeholder="0.00"
        value={form[field.key]}
        onChange={e => handleChange(field.key, e.target.value)}
      />
    </div>
  );

  return (
    <div className="limit-setup-page">
      <h2 className="fixed-income-header">Counterparty Limit Setup</h2>

      {toast && (
        <div className={`alert alert-${toast.type}`}>{toast.message}</div>
      )}

      {!loadingCounterparties && loadError && (
        <div className="alert alert-danger">
          {loadError}{' '}
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginLeft: 8 }}
            onClick={fetchCounterparties}
          >
            Retry
          </button>
        </div>
      )}

      {isApprover && pending.length > 0 && (
        <div className="transaction-form-container">
          <h3 className="limit-section-title">
            Pending Approvals
            <span className="limit-section-hint">
              {pending.length} proposal{pending.length === 1 ? '' : 's'} awaiting your review
            </span>
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Counterparty</th>
                <th>Overall Exposure</th>
                <th>Currency</th>
                <th>Proposed By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <React.Fragment key={p.id}>
                  <tr>
                    <td>{counterpartyName(p)}</td>
                    <td>{Number(p.payload?.overall_exposure_limit || 0).toLocaleString()}</td>
                    <td>{p.payload?.currency_limit || p.payload?.currency || '-'}</td>
                    <td>{p.proposed_by_name || `user #${p.proposed_by}`}</td>
                    <td>
                      <span className="mo-status mo-status--pending">Pending approval</span>
                    </td>
                  </tr>
                  {expandedId === p.id && (
                    <tr className="mo-pending-row">
                      <td colSpan={5}>
                        <div className="limit-request-detail">
                          {detailFields.map((f) => {
                            const raw = p.payload?.[f.key];
                            if (raw === undefined || raw === null || raw === '') return null;
                            return (
                              <div className="limit-request-detail-item" key={f.key}>
                                <span className="limit-request-detail-label">{f.label}</span>
                                <span className="limit-request-detail-value">
                                  {f.key === 'currency_limit' ? raw : formatAmount(raw)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr className="mo-pending-row">
                    <td colSpan={5}>
                      <div className="mo-pending-actions">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                        >
                          {expandedId === p.id ? 'Hide Limits' : 'View Limits'}
                        </button>
                        <button type="button" className="btn btn-success" onClick={() => handleApprove(p.id)}>
                          Approve
                        </button>
                        {rejectingId === p.id ? (
                          <>
                            <input
                              className="form-control"
                              style={{ maxWidth: 280 }}
                              placeholder="Reason for rejection (optional)"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                            />
                            <button type="button" className="btn btn-danger" onClick={() => handleReject(p.id)}>
                              Confirm Reject
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button type="button" className="btn btn-danger" onClick={() => setRejectingId(p.id)}>
                            Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="transaction-form-container">

          <div className="limit-section">
            <h3 className="limit-section-title">Counterparty &amp; Overall Exposure</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label" htmlFor="counterparty">Counterparty Name</label>
                <select
                  id="counterparty"
                  className="form-control"
                  value={form.counterparty_id ? `${form.counterparty_id}__${form.counterparty_type}` : ''}
                  onChange={handleCounterpartyChange}
                  required
                  disabled={loadingCounterparties}
                >
                  <option value="">
                    {loadingCounterparties ? 'Loading counterparties...' : 'Select Counterparty'}
                  </option>
                  {COUNTERPARTY_GROUPS.map(group => {
                    const items = counterparties.filter(cp => cp.type === group.type);
                    if (items.length === 0) return null;
                    return (
                      <optgroup key={group.type} label={group.label}>
                        {/* The endpoint returns id/name/type - there is no
                            unique_id field, which the previous version read. */}
                        {items.map(cp => (
                          <option key={`${cp.type}-${cp.id}`} value={`${cp.id}__${cp.type}`}>
                            {cp.name}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="overall_exposure_limit">Overall Exposure Limit</label>
                <input
                  id="overall_exposure_limit"
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.overall_exposure_limit}
                  onChange={e => handleChange('overall_exposure_limit', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="currency_limit">Currency Limit</label>
                <select
                  id="currency_limit"
                  className="form-control"
                  value={form.currency_limit}
                  onChange={e => handleChange('currency_limit', e.target.value)}
                >
                  <option value="">Select Currency</option>
                  {currencyOptions.map(cur => <option key={cur} value={cur}>{cur}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="limit-section">
            <h3 className="limit-section-title">
              Product Limits
              <span className="limit-section-hint">Leave blank where no limit applies</span>
            </h3>
            <div className="form-grid">
              {productFields.map(renderNumberField)}
            </div>
          </div>

          <div className="limit-section">
            <h3 className="limit-section-title">Other Limits</h3>
            <div className="form-grid">
              {otherLimitFields.map(renderNumberField)}
            </div>
          </div>

          <div className="limit-setup-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? 'Saving...'
                : tier === 'middle_office_user' ? 'Submit for Approval' : 'Save Limit'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setForm(initialState)}
              disabled={saving}
            >
              Clear
            </button>
          </div>

        </div>
      </form>
    </div>
  );
}
